/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import { EMPTY_ARRAY } from '@/redux/selectors/pieveSelectors/constants.js';
import { isDraftThreadActive } from '@/redux/selectors/pieveSelectors/activeThreadIdSelector.js';

const rawKnowledge = (state) => state?.pieve?.knowledge || EMPTY_ARRAY;

const getMeta = (row) => (row?.meta && typeof row.meta === 'object' ? row.meta : {});
const getTitle = (row) => getMeta(row).title || row?.name || row?.id || '';

export const knowledgeItems = rawKnowledge;

export const knowledgeSkillItems = createSelector([rawKnowledge], (rows) => {
  return rows.filter((row) => {
    const meta = getMeta(row);
    return meta.documentKind === 'skill' && (meta.status || 'active') === 'active' && row?.isShareInvitation !== true;
  });
});

export const sharedSkillInvitationItems = createSelector([rawKnowledge], (rows) => {
  const invitations = rows.filter((row) => {
    return getMeta(row).documentKind === 'skill' && row?.isShareInvitation === true;
  });

  return [...invitations].sort((a, b) => {
    const bTime = Date.parse(b.createdOn || b.updatedOn || 0);
    const aTime = Date.parse(a.createdOn || a.updatedOn || 0);

    if (Number.isFinite(bTime) && Number.isFinite(aTime) && bTime !== aTime) {
      return bTime - aTime;
    }

    return getTitle(a).localeCompare(getTitle(b));
  });
});

export const availableKnowledgeSkillItems = createSelector([knowledgeSkillItems], (items) => {
  return [...items].sort((a, b) => getTitle(a).localeCompare(getTitle(b)));
});

export const pinnedKnowledgeSkillItems = createSelector([knowledgeSkillItems], (items) => {
  const pinned = items.filter((item) => Boolean(item?.pinnedOn));

  return [...pinned].sort((a, b) => {
    const bTime = Date.parse(b.pinnedOn || b.updatedOn || 0);
    const aTime = Date.parse(a.pinnedOn || a.updatedOn || 0);

    if (Number.isFinite(bTime) && Number.isFinite(aTime) && bTime !== aTime) {
      return bTime - aTime;
    }

    return getTitle(a).localeCompare(getTitle(b));
  });
});

export const knowledgeItemById = (state, id) => {
  return rawKnowledge(state).find((row) => row?.id === id) || null;
};

export const activeSkillId = (state) => {
  if (!isDraftThreadActive(state)) {
    return null;
  }

  const search = String(state?.router?.location?.search || '');
  const params = new URLSearchParams(search);
  return params.get('skillId') || null;
};

// Backwards-compat aliases
export const storedSkillItems = knowledgeSkillItems;
export const skillItems = knowledgeSkillItems;
export const availableSkillItems = availableKnowledgeSkillItems;
export const pinnedSkillItems = pinnedKnowledgeSkillItems;
